import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";

/** Radware product lines used for internal docs and Q&A family filters. */
const RADWARE_PRODUCT_FAMILIES = [
  "Alteon",
  "API Security",
  "Agentic AI",
  "DefensePro",
  "Cloud DDoS",
  "Cloud WAF",
  "K8S WAF",
  "CyberController",
  "General Topics",
];

/** Competitor vendors available in this UI (lowercase); product_family is forced to match vendor. */
const COMPETITIVE_VENDOR_SLUGS = ["arbor", "f5", "fortinet", "akamai", "imperva"];

/** Search tab: Radware families + competitor keys offered in the library UI. */
const SEARCH_FAMILY_OPTIONS = [...RADWARE_PRODUCT_FAMILIES, ...COMPETITIVE_VENDOR_SLUGS];

const DOCUMENT_TYPES = ["User Guide", "Release Notes", "Datasheet", "Techspec", "other"];

/** Upload + row vendor dropdown. */
const VENDOR_SELECT_OPTIONS = ["radware", "arbor", "f5", "fortinet", "akamai", "imperva"];

function isCompetitiveVendor(vendor) {
  const s = String(vendor || "").trim().toLowerCase();
  return COMPETITIVE_VENDOR_SLUGS.includes(s);
}

function competitiveFamiliesFromVendor(vendor) {
  const v = String(vendor || "").trim().toLowerCase();
  return v ? [v] : [];
}

/** Legacy uploads used product_family "competitive" for all competitors. */
function isLegacyCompetitiveFamilyToken(pf) {
  return String(pf || "").trim().toLowerCase() === "competitive";
}

/** Visual-only product family editor; keeps value as string[] like the old multi-select. */
function ProductFamilyPicker({ value, onChange, disabled, familyOptions = RADWARE_PRODUCT_FAMILIES }) {
  const selected = Array.isArray(value) ? value : [];
  const add = (pf) => {
    if (disabled || selected.includes(pf)) return;
    onChange([...selected, pf]);
  };
  const remove = (pf) => {
    if (disabled) return;
    onChange(selected.filter((x) => x !== pf));
  };
  const available = familyOptions.filter((pf) => !selected.includes(pf));

  return (
    <div className="space-y-2">
      <div className="flex min-h-[2rem] flex-wrap gap-1.5">
        {selected.length === 0 && (
          <span className="text-xs text-slate-500 dark:text-slate-400">None selected</span>
        )}
        {selected.map((pf) => (
          <span
            key={pf}
            className="inline-flex items-center gap-1 rounded-full border border-blue-200 bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-900 dark:border-blue-800 dark:bg-blue-950/50 dark:text-blue-200"
          >
            {pf}
            <button
              type="button"
              disabled={disabled}
              onClick={() => remove(pf)}
              className="rounded-full px-0.5 leading-none hover:bg-blue-200/80 dark:hover:bg-blue-900/80"
              aria-label={`Remove ${pf}`}
            >
              ×
            </button>
          </span>
        ))}
      </div>
      <div className="flex flex-wrap gap-1.5">
        {available.map((pf) => (
          <button
            key={pf}
            type="button"
            disabled={disabled}
            onClick={() => add(pf)}
            className="rounded-full border border-slate-300 bg-white px-2.5 py-1 text-xs font-medium text-slate-700 hover:border-blue-400 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-blue-500 dark:hover:bg-slate-700/80"
          >
            + {pf}
          </button>
        ))}
      </div>
    </div>
  );
}

function docProductFamiliesList(doc) {
  if (Array.isArray(doc.product_families) && doc.product_families.length) return doc.product_families;
  if (doc.product_family) return [doc.product_family];
  return [];
}

function ProductFamilyChipsDisplay({ families }) {
  const list = Array.isArray(families) && families.length ? families : [];
  if (!list.length) {
    return <span className="text-slate-400">—</span>;
  }
  return (
    <div className="flex flex-wrap gap-1">
      {list.map((pf) => (
        <span
          key={pf}
          className="inline-block rounded-full border border-slate-200 bg-slate-100 px-2 py-0.5 text-xs text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200"
        >
          {pf}
        </span>
      ))}
    </div>
  );
}

export default function RdwrDocs() {
  const [activeTab, setActiveTab] = useState("manage");
  const [files, setFiles] = useState([]);
  const [vendor, setVendor] = useState("radware");
  const [productFamilies, setProductFamilies] = useState(["General Topics"]);
  const [documentType, setDocumentType] = useState("User Guide");
  const [applyMetadataToAll, setApplyMetadataToAll] = useState(true);

  const [documents, setDocuments] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [rowEdits, setRowEdits] = useState({});
  const [bulkVendor, setBulkVendor] = useState("");
  const [bulkProductFamilies, setBulkProductFamilies] = useState([]);
  const [bulkDocumentType, setBulkDocumentType] = useState("");

  const [isUploading, setIsUploading] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isReloadingRetrieval, setIsReloadingRetrieval] = useState(false);
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [searchFamily, setSearchFamily] = useState("");
  const [searchDocumentType, setSearchDocumentType] = useState("");
  const [searchQuery, setSearchQuery] = useState("");

  const API_BASE = "/api";

  const formatApiError = (err, fallback) => {
    const status = err?.response?.status;
    const detail = err?.response?.data?.detail;
    const detailText = Array.isArray(detail)
      ? detail.map((d) => d?.msg || JSON.stringify(d)).join("; ")
      : typeof detail === "string"
      ? detail
      : detail
      ? JSON.stringify(detail)
      : err?.message;
    if (status && detailText) return `❌ ${fallback} (HTTP ${status}: ${detailText})`;
    if (status) return `❌ ${fallback} (HTTP ${status})`;
    if (detailText) return `❌ ${fallback}: ${detailText}`;
    return `❌ ${fallback}.`;
  };

  const getStatusBadgeClass = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "embedded") return "border-green-300 bg-green-100 text-green-800 dark:border-green-700 dark:bg-green-950/40 dark:text-green-300";
    if (s === "chunked") return "border-blue-300 bg-blue-100 text-blue-800 dark:border-blue-700 dark:bg-blue-950/40 dark:text-blue-300";
    if (s === "extracted") return "border-indigo-300 bg-indigo-100 text-indigo-800 dark:border-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300";
    if (s === "uploaded") return "border-slate-300 bg-slate-100 text-slate-800 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200";
    if (s.endsWith("_failed")) return "border-red-300 bg-red-100 text-red-800 dark:border-red-700 dark:bg-red-950/40 dark:text-red-300";
    if (s === "in progress" || s === "in_progress") return "border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300";
    if (s === "deleted") return "border-slate-300 bg-slate-100 text-slate-600 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-300";
    return "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200";
  };

  const getMetadataBadgeClass = (status) => {
    const s = String(status || "").toLowerCase();
    if (s === "ready_for_extraction") return "border-emerald-300 bg-emerald-100 text-emerald-800 dark:border-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300";
    if (s === "metadata_pending") return "border-amber-300 bg-amber-100 text-amber-800 dark:border-amber-700 dark:bg-amber-950/40 dark:text-amber-300";
    return "border-slate-300 bg-slate-100 text-slate-700 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200";
  };

  const loadDocuments = async () => {
    try {
      const res = await axios.get(`${API_BASE}/documents`);
      const rows = Array.isArray(res.data) ? res.data : [];
      setDocuments(rows);
      const init = {};
      for (const d of rows) {
        const pfs = Array.isArray(d.product_families) && d.product_families.length
          ? d.product_families
          : d.product_family
          ? [d.product_family]
          : [];
        init[d.document_id] = {
          vendor: d.vendor || "",
          product_families: pfs,
          document_type: d.document_type || "",
        };
      }
      setRowEdits(init);
    } catch (err) {
      setMessage(formatApiError(err, "Failed to load document library"));
    }
  };

  useEffect(() => {
    loadDocuments();
  }, []);

  const allSelected = useMemo(
    () => documents.length > 0 && selectedIds.length === documents.length,
    [documents.length, selectedIds.length]
  );

  const searchResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return documents.filter((doc) => {
      const families =
        Array.isArray(doc.product_families) && doc.product_families.length
          ? doc.product_families
          : doc.product_family
          ? [doc.product_family]
          : [];

      const familyOk = !searchFamily || families.some((f) => String(f).toLowerCase() === searchFamily.toLowerCase());
      const typeOk = !searchDocumentType || String(doc.document_type || "").toLowerCase() === searchDocumentType.toLowerCase();
      const queryOk =
        !q ||
        String(doc.filename || "").toLowerCase().includes(q) ||
        String(doc.vendor || "").toLowerCase().includes(q);

      return familyOk && typeOk && queryOk;
    });
  }, [documents, searchFamily, searchDocumentType, searchQuery]);

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!files.length) {
      setMessage("❌ Please choose at least one file.");
      return;
    }

    const formData = new FormData();
    files.forEach((f) => formData.append("files", f));
    formData.append("collection", "wiki_documents");
    formData.append("vendor", vendor);
    formData.append("apply_metadata_to_all", String(applyMetadataToAll));
    if (applyMetadataToAll) {
      formData.append("product_families", JSON.stringify(productFamilies));
      formData.append("product_family", productFamilies[0] || "");
      formData.append("document_type", documentType);
    }

    setIsUploading(true);
    setMessage("⏳ Uploading documents...");
    try {
      const res = await axios.post(`${API_BASE}/documents/upload`, formData);
      const count = res.data?.length || 0;
      setMessage(
        applyMetadataToAll
          ? `✅ Uploaded ${count} document(s) with shared metadata.`
          : `✅ Uploaded ${count} document(s). Metadata review is pending.`
      );
      setFiles([]);
      await loadDocuments();
    } catch (err) {
      setMessage(formatApiError(err, "Upload failed"));
    } finally {
      setIsUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    const sourceUri = String(doc?.source_uri || "").trim();
    if (String(doc?.source_type || "").trim() === "web_page" && sourceUri) {
      window.open(sourceUri, "_blank", "noopener,noreferrer");
      return;
    }
    try {
      const res = await axios.get(`${API_BASE}/documents/${doc.document_id}/download`, {
        responseType: "blob",
      });
      const blobUrl = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = doc.filename || "document";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      setMessage(formatApiError(err, "Download failed"));
    }
  };

  const handleDelete = async (doc) => {
    const ok = window.confirm(`Delete "${doc.filename}"?`);
    if (!ok) return;
    try {
      await axios.delete(`${API_BASE}/documents/${doc.document_id}`);
      setMessage("✅ Document deleted.");
      setSelectedIds((prev) => prev.filter((id) => id !== doc.document_id));
      await loadDocuments();
    } catch (_err) {
      setMessage("❌ Delete failed.");
    }
  };

  const setRowField = (documentId, key, value) => {
    if (key === "vendor" && isCompetitiveVendor(value)) {
      setRowEdits((prev) => ({
        ...prev,
        [documentId]: {
          ...(prev[documentId] || {}),
          vendor: value,
          product_families: competitiveFamiliesFromVendor(value),
        },
      }));
      return;
    }
    setRowEdits((prev) => ({
      ...prev,
      [documentId]: {
        ...(prev[documentId] || {}),
        [key]: value,
      },
    }));
  };

  const saveRowMetadata = async (documentId) => {
    const patch = rowEdits[documentId] || {};
    try {
      await axios.patch(`${API_BASE}/documents/${documentId}/metadata`, patch);
      setMessage("✅ Document metadata updated.");
      await loadDocuments();
    } catch (_err) {
      setMessage("❌ Failed to update document metadata.");
    }
  };

  const toggleSelectRow = (documentId, checked) => {
    setSelectedIds((prev) => {
      if (checked) {
        return prev.includes(documentId) ? prev : [...prev, documentId];
      }
      return prev.filter((id) => id !== documentId);
    });
  };

  const toggleSelectAll = (checked) => {
    if (checked) {
      setSelectedIds(documents.map((d) => d.document_id));
    } else {
      setSelectedIds([]);
    }
  };

  const runBulkMetadataUpdate = async () => {
    if (!selectedIds.length) {
      setMessage("❌ Select at least one document for bulk update.");
      return;
    }

    const patch = {};
    if (bulkVendor.trim()) patch.vendor = bulkVendor.trim();
    if (bulkProductFamilies.length) patch.product_families = bulkProductFamilies;
    if (bulkDocumentType.trim()) patch.document_type = bulkDocumentType.trim();

    if (!Object.keys(patch).length) {
      setMessage("❌ Provide at least one metadata field in bulk edit.");
      return;
    }

    setIsBulkSaving(true);
    try {
      await axios.post(`${API_BASE}/documents/metadata/bulk`, {
        document_ids: selectedIds,
        patch,
      });
      setMessage(`✅ Bulk metadata updated for ${selectedIds.length} document(s).`);
      setSelectedIds([]);
      setBulkVendor("");
      setBulkProductFamilies([]);
      setBulkDocumentType("");
      await loadDocuments();
    } catch (_err) {
      setMessage("❌ Bulk metadata update failed.");
    } finally {
      setIsBulkSaving(false);
    }
  };

  const handleProcessDocuments = async () => {
    await runPipelineJob(false);
  };

  const handleReExtractAll = async () => {
    const ok = window.confirm("Re-process all documents (extract + chunk + embed)? This may take longer.");
    if (!ok) return;
    await runPipelineJob(true);
  };

  const handleReloadRetrieval = async () => {
    setIsReloadingRetrieval(true);
    setMessage("⏳ Reloading document retrieval cache...");
    try {
      const res = await axios.post(`${API_BASE}/documents/reload-retrieval`);
      const n = res.data?.index_ntotal ?? "?";
      setMessage(`✅ QA will use the latest document index (${n} vectors).`);
    } catch (_err) {
      setMessage("❌ Failed to reload document retrieval cache.");
    } finally {
      setIsReloadingRetrieval(false);
    }
  };

  const runPipelineJob = async (forceAll) => {
    setIsProcessing(true);
    setMessage(
      forceAll
        ? "⚙️ Queuing full document pipeline for all documents..."
        : "⚙️ Queuing document pipeline... (pending/failed stages only)"
    );
    try {
      const start = await axios.post(`${API_BASE}/documents/process`, null, {
        params: { mode: forceAll ? "force_all" : "pending" },
      });
      const jobId = start.data?.job_id;
      if (!jobId) {
        throw new Error("No job id returned");
      }

      let done = false;
      while (!done) {
        await new Promise((r) => setTimeout(r, 3000));
        const statusRes = await axios.get(`${API_BASE}/documents/process-status/${jobId}`);
        const status = statusRes.data?.status;
        const stage = statusRes.data?.stage;

        if (status === "queued" || status === "in progress") {
          setMessage(`⚙️ Processing documents... (${status}${stage ? `, stage: ${stage}` : ""})`);
          continue;
        }

        if (status === "completed") {
          const summary = statusRes.data?.summary || {};
          const ext = summary.extract || {};
          const chk = summary.chunk || {};
          const emb = summary.embed || {};
          setMessage(
            `✅ Pipeline finished. Extracted: ${ext.extracted || 0}/${ext.processed || 0}, ` +
              `chunked: ${chk.chunked || 0}/${chk.processed || 0}, ` +
              `embedded: ${emb.embedded || 0}/${emb.processed || 0}.`
          );
          done = true;
          break;
        }

        if (status === "failed") {
          setMessage(`❌ Document pipeline failed at stage "${stage || "unknown"}": ${statusRes.data?.error || "Unknown error"}`);
          done = true;
          break;
        }

        setMessage("❌ Document pipeline failed: unexpected job status.");
        done = true;
      }

      await loadDocuments();
    } catch (_err) {
      setMessage("❌ Document pipeline failed.");
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="w-full min-w-0">
      <div className="mb-6">
        <h1 className="text-2xl font-semibold tracking-tight text-slate-900 dark:text-white sm:text-3xl">
          Document library
        </h1>
        <p className="mt-1 max-w-3xl text-sm text-slate-600 dark:text-slate-400">
          Upload, process, and curate document metadata used by Q&amp;A retrieval.
        </p>
      </div>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <button
          type="button"
          className={`rounded border px-4 py-2 text-sm font-medium transition ${
            activeTab === "manage"
              ? "border-blue-600 bg-blue-600 text-white"
              : "border-slate-300 bg-slate-100 text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          }`}
          onClick={() => setActiveTab("manage")}
        >
          Manage
        </button>
        <button
          type="button"
          className={`rounded border px-4 py-2 text-sm font-medium transition ${
            activeTab === "search"
              ? "border-blue-600 bg-blue-600 text-white"
              : "border-slate-300 bg-slate-100 text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
          }`}
          onClick={() => setActiveTab("search")}
        >
          Search
        </button>
      </div>

      {activeTab === "search" && (
        <div className="mb-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-600 dark:bg-slate-900/80">
          <h2 className="mb-2 text-lg font-semibold text-slate-900 dark:text-white">Search documents</h2>
          <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
            Filter quickly by product family and document type.
          </p>

          <div className="mb-4 grid grid-cols-1 gap-3 md:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-600 dark:text-slate-300">
                Product family
              </label>
              <select
                className="w-full rounded border border-slate-300 bg-white p-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                value={searchFamily}
                onChange={(e) => setSearchFamily(e.target.value)}
              >
                <option value="">All product families</option>
                {SEARCH_FAMILY_OPTIONS.map((pf) => (
                  <option key={pf} value={pf}>
                    {pf}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-600 dark:text-slate-300">
                Document type
              </label>
              <select
                className="w-full rounded border border-slate-300 bg-white p-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                value={searchDocumentType}
                onChange={(e) => setSearchDocumentType(e.target.value)}
              >
                <option value="">All document types</option>
                {DOCUMENT_TYPES.map((dt) => (
                  <option key={dt} value={dt}>
                    {dt}
                  </option>
                ))}
              </select>
            </div>
            <div className="md:col-span-2">
              <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-600 dark:text-slate-300">
                Filename / vendor (optional)
              </label>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Type part of a filename..."
                className="w-full rounded border border-slate-300 bg-white p-2 text-sm text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
              />
            </div>
          </div>

          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-slate-700 dark:text-slate-300">
              {searchResults.length} result{searchResults.length === 1 ? "" : "s"}
            </p>
            <button
              type="button"
              onClick={() => {
                setSearchFamily("");
                setSearchDocumentType("");
                setSearchQuery("");
              }}
              className="rounded border border-slate-300 bg-slate-100 px-3 py-1.5 text-sm text-slate-900 hover:bg-slate-200 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
            >
              Clear filters
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[960px] border border-slate-300 text-sm dark:border-slate-700">
              <thead className="bg-slate-100 dark:bg-slate-800/80">
                <tr>
                  <th className="border border-slate-300 px-3 py-2 text-left dark:border-slate-700">Filename</th>
                  <th
                    className="border border-slate-300 px-3 py-2 text-left dark:border-slate-700"
                    title="Radware product lines or competitor key (matches vendor for competitor docs)"
                  >
                    Product family
                  </th>
                  <th className="border border-slate-300 px-3 py-2 text-left dark:border-slate-700">Document Type</th>
                  <th className="border border-slate-300 px-3 py-2 text-left dark:border-slate-700">Status</th>
                  <th className="border border-slate-300 px-3 py-2 text-left dark:border-slate-700">Upload Date</th>
                  <th className="border border-slate-300 px-3 py-2 text-left dark:border-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((doc) => {
                  return (
                    <tr key={`search-${doc.document_id}`} className="odd:bg-white even:bg-slate-50 dark:odd:bg-slate-900/30 dark:even:bg-slate-900/60">
                      <td className="border border-slate-300 px-3 py-2 dark:border-slate-700">{doc.filename}</td>
                      <td className="border border-slate-300 px-3 py-2 dark:border-slate-700">
                        <ProductFamilyChipsDisplay families={docProductFamiliesList(doc)} />
                      </td>
                      <td className="border border-slate-300 px-3 py-2 dark:border-slate-700">{doc.document_type || "-"}</td>
                      <td className="border border-slate-300 px-3 py-2 dark:border-slate-700">
                        <span
                          className={`inline-flex items-center rounded border px-2 py-0.5 text-xs font-medium ${getStatusBadgeClass(
                            doc.status
                          )}`}
                        >
                          {doc.status || "unknown"}
                        </span>
                      </td>
                      <td className="border border-slate-300 px-3 py-2 dark:border-slate-700">
                        {new Date(doc.upload_date).toLocaleString()}
                      </td>
                      <td className="border border-slate-300 px-3 py-2 whitespace-nowrap dark:border-slate-700">
                        <button
                          className="mr-2 rounded bg-blue-600 px-2 py-1 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500"
                          onClick={() => handleDownload(doc)}
                        >
                          Download
                        </button>
                        <button
                          className="rounded bg-red-600 px-2 py-1 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-500"
                          onClick={() => handleDelete(doc)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  );
                })}
                {searchResults.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="border border-slate-300 px-3 py-3 text-center text-slate-500 dark:border-slate-700 dark:text-slate-400"
                    >
                      No documents match your current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === "manage" && (
        <>
      <div className="mb-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-600 dark:bg-slate-900/80">
        <h2 className="text-xl font-semibold mb-4">Upload Documents</h2>
        <form onSubmit={handleUpload} className="space-y-4">
          <div>
            <label className="mb-2 block text-sm font-medium text-slate-800 dark:text-slate-100">Files (PDF, DOCX, XLS/XLSX)</label>
            <input
              type="file"
              accept=".pdf,.docx,.xls,.xlsx"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="w-full rounded border border-slate-300 bg-white p-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
            />
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-300">
            <input
              type="checkbox"
              checked={applyMetadataToAll}
              onChange={(e) => setApplyMetadataToAll(e.target.checked)}
              className="h-4 w-4 rounded border-slate-400"
            />
            Apply metadata to all files in this batch
          </label>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-800 dark:text-slate-100">Vendor</label>
              <select
                className="w-full rounded border border-slate-300 bg-white p-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                value={vendor}
                onChange={(e) => {
                  const next = e.target.value;
                  setVendor(next);
                  if (isCompetitiveVendor(next)) {
                    setProductFamilies(competitiveFamiliesFromVendor(next));
                  } else if (
                    Array.isArray(productFamilies) &&
                    productFamilies.length === 1 &&
                    (isLegacyCompetitiveFamilyToken(productFamilies[0]) ||
                      isCompetitiveVendor(String(productFamilies[0])))
                  ) {
                    setProductFamilies(["General Topics"]);
                  }
                }}
                disabled={!applyMetadataToAll}
              >
                {VENDOR_SELECT_OPTIONS.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-800 dark:text-slate-100">
                {isCompetitiveVendor(vendor) ? "Retrieval family (vendor)" : "Product family (Radware)"}
              </label>
              <ProductFamilyPicker
                value={productFamilies}
                onChange={setProductFamilies}
                disabled={!applyMetadataToAll || isCompetitiveVendor(vendor)}
              />
              <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
                {isCompetitiveVendor(vendor)
                  ? "For competitor uploads, the retrieval family is set to the vendor key (e.g. arbor) and matches the competitive index bucket."
                  : "Click + to add a family; remove with × on the chip."}
              </p>
            </div>
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-800 dark:text-slate-100">Document Type</label>
              <select
                className="w-full rounded border border-slate-300 bg-white p-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                value={documentType}
                onChange={(e) => setDocumentType(e.target.value)}
                disabled={!applyMetadataToAll}
              >
                {DOCUMENT_TYPES.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="text-sm text-slate-600 dark:text-slate-400">
            <div>
              <strong>collection:</strong> wiki_documents
            </div>
            {!applyMetadataToAll && (
              <div className="text-amber-700 dark:text-amber-300">
                Uploaded files will be marked as <strong>metadata_pending</strong> until metadata is filled in the table.
              </div>
            )}
          </div>

          <button
            type="submit"
            disabled={isUploading}
            className={`rounded px-4 py-2 text-sm font-medium text-white ${
              isUploading ? "bg-slate-500" : "bg-green-600 hover:bg-green-700 dark:bg-green-600 dark:hover:bg-green-500"
            }`}
          >
            {isUploading ? "⏳ Uploading..." : "📤 Upload Batch"}
          </button>
        </form>
      </div>

      <div className="mb-8 rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-600 dark:bg-slate-900/80">
        <h2 className="text-xl font-semibold mb-3">Processing Pipeline</h2>
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
          Runs the end-to-end pipeline for uploaded documents: extraction (Docling) → chunking → embeddings/index artifacts.
          Documents marked <strong>metadata_pending</strong> are skipped until metadata is complete.
        </p>
        <p className="mb-4 text-sm text-slate-600 dark:text-slate-400">
          After new embeddings are written, the server reloads the document index automatically when the pipeline finishes.
          Use <strong>Reload QA document index</strong> if you changed index files another way or QA still looks stale.
        </p>
        <button
          type="button"
          disabled={isProcessing}
          onClick={handleProcessDocuments}
          className={`rounded px-4 py-2 text-sm font-medium text-white ${
            isProcessing ? "bg-slate-500" : "bg-purple-600 hover:bg-purple-700 dark:bg-purple-600 dark:hover:bg-purple-500"
          }`}
        >
          {isProcessing ? "⏳ Processing..." : "⚙️ Process Pending Docs"}
        </button>
        <button
          type="button"
          disabled={isProcessing}
          onClick={handleReExtractAll}
          className={`ml-3 rounded px-4 py-2 text-sm font-medium text-white ${
            isProcessing ? "bg-slate-500" : "bg-indigo-600 hover:bg-indigo-700 dark:bg-indigo-600 dark:hover:bg-indigo-500"
          }`}
        >
          ♻️ Re-Process All
        </button>
        <button
          type="button"
          disabled={isProcessing || isReloadingRetrieval}
          onClick={handleReloadRetrieval}
          className={`ml-3 rounded px-4 py-2 text-sm font-medium text-white ${
            isProcessing || isReloadingRetrieval ? "bg-slate-500" : "bg-teal-600 hover:bg-teal-700 dark:bg-teal-600 dark:hover:bg-teal-500"
          }`}
        >
          {isReloadingRetrieval ? "⏳ Reloading..." : "🔄 Reload QA document index"}
        </button>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-600 dark:bg-slate-900/80">
        <h2 className="text-xl font-semibold mb-4">Uploaded Documents</h2>

        <div className="mb-4 rounded border border-slate-200 bg-slate-50 p-3 dark:border-slate-700 dark:bg-slate-900/70">
          <div className="mb-2 font-medium text-slate-900 dark:text-slate-100">Bulk edit selected</div>
          <datalist id="vendor-options">
            {VENDOR_SELECT_OPTIONS.map((v) => (
              <option key={v} value={v} />
            ))}
          </datalist>
          <div className="flex flex-col gap-3 xl:flex-row xl:items-end xl:gap-3">
            <div className="min-w-0 xl:w-44">
              <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">Vendor</label>
              <input
                className="w-full rounded border border-slate-300 bg-white p-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                value={bulkVendor}
                onChange={(e) => setBulkVendor(e.target.value)}
                list="vendor-options"
              />
            </div>
            <div className="min-w-0 xl:w-52">
              <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">Document type</label>
              <select
                className="w-full rounded border border-slate-300 bg-white p-2 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                value={bulkDocumentType}
                onChange={(e) => setBulkDocumentType(e.target.value)}
              >
                <option value="">Document Type (no change)</option>
                {DOCUMENT_TYPES.map((v) => (
                  <option key={v} value={v}>
                    {v}
                  </option>
                ))}
              </select>
            </div>
            <div className="min-w-0 flex-1">
              <label className="mb-1 block text-xs font-medium text-slate-600 dark:text-slate-400">Product families</label>
              <ProductFamilyPicker
                value={bulkProductFamilies}
                onChange={setBulkProductFamilies}
                familyOptions={SEARCH_FAMILY_OPTIONS}
              />
            </div>
            <button
              type="button"
              onClick={runBulkMetadataUpdate}
              disabled={isBulkSaving || !selectedIds.length}
              className={`shrink-0 rounded px-3 py-2 text-sm font-medium text-white xl:self-end ${
                isBulkSaving || !selectedIds.length
                  ? "bg-slate-400"
                  : "bg-teal-600 hover:bg-teal-700 dark:bg-teal-600 dark:hover:bg-teal-500"
              }`}
            >
              {isBulkSaving ? "Saving..." : `Apply to ${selectedIds.length} selected`}
            </button>
          </div>
          <div className="mt-2 text-xs text-slate-500 dark:text-slate-400">
            Radware lines and Arbor (as retrieval family) are listed here; leave empty to skip changing families.
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1320px] border border-slate-300 text-sm dark:border-slate-700">
            <thead className="bg-slate-100 dark:bg-slate-800/80">
              <tr>
                <th className="border border-slate-300 px-3 py-2 text-left dark:border-slate-700">
                  <input type="checkbox" checked={allSelected} onChange={(e) => toggleSelectAll(e.target.checked)} />
                </th>
                <th className="border border-slate-300 px-3 py-2 text-left dark:border-slate-700">Filename</th>
                <th className="border border-slate-300 px-3 py-2 text-left dark:border-slate-700">Vendor</th>
                <th
                  className="border border-slate-300 px-3 py-2 text-left dark:border-slate-700"
                  title="Radware product lines, or competitor key (same as vendor for competitor docs)"
                >
                  Product family
                </th>
                <th className="border border-slate-300 px-3 py-2 text-left dark:border-slate-700">Document Type</th>
                <th className="border border-slate-300 px-3 py-2 text-left dark:border-slate-700">Upload Date</th>
                <th className="border border-slate-300 px-3 py-2 text-left dark:border-slate-700">Metadata</th>
                <th className="border border-slate-300 px-3 py-2 text-left dark:border-slate-700">Status</th>
                <th className="border border-slate-300 px-3 py-2 text-left dark:border-slate-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {documents.map((doc) => {
                const edit = rowEdits[doc.document_id] || { vendor: "", product_families: [], document_type: "" };
                return (
                  <tr key={doc.document_id} className="odd:bg-white even:bg-slate-50 dark:odd:bg-slate-900/30 dark:even:bg-slate-900/60">
                    <td className="border border-slate-300 px-3 py-2 dark:border-slate-700">
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(doc.document_id)}
                        onChange={(e) => toggleSelectRow(doc.document_id, e.target.checked)}
                      />
                    </td>
                    <td className="border border-slate-300 px-3 py-2 dark:border-slate-700">{doc.filename}</td>
                    <td className="border border-slate-300 px-3 py-2 dark:border-slate-700">
                      <select
                        className="w-36 rounded border border-slate-300 bg-white p-1 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                        value={edit.vendor}
                        onChange={(e) => setRowField(doc.document_id, "vendor", e.target.value)}
                      >
                        <option value="">(none)</option>
                        {VENDOR_SELECT_OPTIONS.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="min-w-[13rem] max-w-[22rem] border border-slate-300 px-3 py-2 align-top dark:border-slate-700">
                      <ProductFamilyPicker
                        value={edit.product_families || []}
                        onChange={(next) => setRowField(doc.document_id, "product_families", next)}
                        disabled={isCompetitiveVendor(edit.vendor)}
                        familyOptions={RADWARE_PRODUCT_FAMILIES}
                      />
                      {isCompetitiveVendor(edit.vendor) && (
                        <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400">
                          Family is locked to vendor key for retrieval (e.g. arbor).
                        </p>
                      )}
                      <div className="mt-2 border-t border-slate-200 pt-2 dark:border-slate-600">
                        <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
                          Stored
                        </div>
                        <ProductFamilyChipsDisplay families={docProductFamiliesList(doc)} />
                      </div>
                    </td>
                    <td className="border border-slate-300 px-3 py-2 dark:border-slate-700">
                      <select
                        className="w-36 rounded border border-slate-300 bg-white p-1 text-slate-900 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                        value={edit.document_type}
                        onChange={(e) => setRowField(doc.document_id, "document_type", e.target.value)}
                      >
                        <option value="">(none)</option>
                        {DOCUMENT_TYPES.map((v) => (
                          <option key={v} value={v}>
                            {v}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="border border-slate-300 px-3 py-2 dark:border-slate-700">{new Date(doc.upload_date).toLocaleString()}</td>
                    <td className="border border-slate-300 px-3 py-2 dark:border-slate-700">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${getMetadataBadgeClass(
                          doc.metadata_status
                        )}`}
                      >
                        {doc.metadata_status || "metadata_pending"}
                      </span>
                    </td>
                    <td className="border border-slate-300 px-3 py-2 dark:border-slate-700">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded border text-xs font-medium ${getStatusBadgeClass(
                          doc.status
                        )}`}
                      >
                        {doc.status || "unknown"}
                      </span>
                    </td>
                    <td className="border border-slate-300 px-3 py-2 whitespace-nowrap dark:border-slate-700">
                      <button
                        className="mr-2 rounded bg-emerald-600 px-2 py-1 text-white hover:bg-emerald-700 dark:bg-emerald-600 dark:hover:bg-emerald-500"
                        onClick={() => saveRowMetadata(doc.document_id)}
                      >
                        Save
                      </button>
                      <button
                        className="mr-2 rounded bg-blue-600 px-2 py-1 text-white hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-500"
                        onClick={() => handleDownload(doc)}
                      >
                        Download
                      </button>
                      <button
                        className="rounded bg-red-600 px-2 py-1 text-white hover:bg-red-700 dark:bg-red-600 dark:hover:bg-red-500"
                        onClick={() => handleDelete(doc)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })}
              {documents.length === 0 && (
                <tr>
                  <td className="border border-slate-300 px-3 py-2 text-center text-slate-500 dark:border-slate-700 dark:text-slate-400" colSpan={9}>
                    No documents uploaded yet.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      </>
      )}

      {message && (
        <p className="mt-4 rounded border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-200">
          {message}
        </p>
      )}
    </div>
  );
}
